import React from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { TeacherDashboardData } from '@/types/models';
import { dashboardStyles } from '@/layouts/TeacherLayout';

interface OverviewTabProps {
  data: TeacherDashboardData;
}

interface HoursDataItem {
  name: 'Approved' | 'Pending' | 'Rejected';
  value: number;
  startAngle?: number;
  endAngle?: number;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ data }) => {
  const theme = useTheme();

  // Process hours data to ensure complete circle
  const processHoursData = (rawData: HoursDataItem[]): HoursDataItem[] => {
    if (rawData.length === 0) return [];
    if (rawData.length === 1) {
      // For single value, create a full circle with one segment
      return [{
        ...rawData[0],
        startAngle: 0,
        endAngle: 360
      }];
    }
    return rawData;
  };

  // Only include non-zero values in hours data
  const hoursData = processHoursData([
    { name: 'Approved' as const, value: data.class_stats.total_hours_approved },
    { name: 'Pending' as const, value: data.class_stats.total_hours_pending },
    { name: 'Rejected' as const, value: data.class_stats.total_hours_rejected }
  ].filter(item => item.value > 0));

  const applicationData = [
    { name: 'Approved', value: data.class_stats.applications.approved },
    { name: 'Pending', value: data.class_stats.applications.pending },
    { name: 'Rejected', value: data.class_stats.applications.rejected },
    { name: 'Withdrawn', value: data.class_stats.applications.withdrawn }
  ].filter(item => item.value > 0);

  const COLORS = {
    Approved: theme.palette.success.main,
    Pending: theme.palette.warning.main,
    Rejected: theme.palette.error.main,
    Withdrawn: theme.palette.grey[500]
  };

  const StatCard = ({ title, value, subtitle, progress }: { 
    title: string; 
    value: string | number; 
    subtitle?: string;
    progress?: number;
  }) => (
    <Paper sx={{
      ...dashboardStyles.dashboard.analytics.card,
      height: '100%',  // Ensure all cards have same height
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {title}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        )}
        {progress !== undefined && (
          <Box sx={{ mt: 'auto', pt: 2, width: '100%' }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4
                }
              }} 
            />
          </Box>
        )}
      </Box>
    </Paper>
  );

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 2.2;
    const x1 = cx + radius * Math.cos(-midAngle * RADIAN);
    const y1 = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <g>
        <text
          x={x1}
          y={y1}
          fill={theme.palette.text.primary}
          textAnchor={x1 > cx ? 'start' : 'end'}
          dominantBaseline="central"
          fontSize="13"
        >
          {`${name}: ${value}h`}
        </text>
      </g>
    );
  };

  return (
    <Box>
      {/* Key Metrics */}
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Students"
            value={data.class_stats.total_students}
            subtitle={`${data.class_stats.active_students} active`}
            progress={(data.class_stats.active_students / data.class_stats.total_students) * 100}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Hours"
            value={data.class_stats.total_hours_approved}
            subtitle={`${data.class_stats.total_hours_pending} pending`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Applications"
            value={data.class_stats.applications.total}
            subtitle={`${data.class_stats.applications.pending} pending`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Review Time"
            value={`${Math.round(data.time_analytics.average_review_time)}h`}
            subtitle="for volunteer logs"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        {/* Hours Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={dashboardStyles.dashboard.analytics.chart}>
            <Typography variant="h6" gutterBottom>
              Hours Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={hoursData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={85}
                  dataKey="value"
                  startAngle={0}
                  endAngle={360}
                  labelLine={{ 
                    stroke: theme.palette.text.secondary,
                    strokeWidth: 1,
                    strokeDasharray: "2 2"
                  }}
                  label={<CustomLabel />}
                  cornerRadius={0}
                  stroke="none"
                >
                  {hoursData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={COLORS[entry.name as keyof typeof COLORS]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Application Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={dashboardStyles.dashboard.analytics.chart}>
            <Typography variant="h6" gutterBottom>
              Application Status
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={applicationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar 
                  dataKey="value" 
                  radius={[4, 4, 0, 0]}
                >
                  {applicationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name as keyof typeof COLORS]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12}>
          <Paper sx={dashboardStyles.dashboard.analytics.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    New Applications (30d)
                  </Typography>
                  <Typography variant="h5">
                    {data.time_analytics.recent_activity.new_applications}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Hours Logged (30d)
                  </Typography>
                  <Typography variant="h5">
                    {data.time_analytics.recent_activity.hours_logged}h
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">
                    Hours Approved (30d)
                  </Typography>
                  <Typography variant="h5">
                    {data.time_analytics.recent_activity.approved_hours}h
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewTab; 